import { useState, type ReactNode } from 'react';
import type { App, Project } from '@hangar/shared';
import { useCan, type Can } from '@/lib/permissions';
import type { RowAction } from '@/components/RowActions';
import { ArchiveAppDialog } from './ArchiveAppDialog';
import { ConnectAppSheet } from './ConnectAppSheet';
import { EditAppSheet } from './EditAppSheet';

interface Options {
  project: Project | undefined;
  /** After "Connect app" (sheet) succeeded, e.g. to select the new app. */
  onConnected?: (app: App) => void;
  /** After an archive, e.g. to move the selection off the archived app. */
  onArchived?: (app: App) => void;
}

export interface AppActions {
  /** `projects:write` on the project. */
  can: Can;
  connect: () => void;
  /** Edit · Archive for one app. */
  actionsFor: (app: App) => RowAction[];
  /** Connect sheet, edit sheet and archive confirmation; render once. */
  overlays: ReactNode;
}

/** Connect, edit and archive apps: shared by Getting started and the Apps & profiles page. */
export function useAppActions({ project, onConnected, onArchived }: Options): AppActions {
  const can = useCan('projects:write', project?.id);
  const [connecting, setConnecting] = useState(false);
  const [editing, setEditing] = useState<App | null>(null);
  const [archiving, setArchiving] = useState<App | null>(null);
  const reason = can.allowed ? undefined : can.reason;

  const actionsFor = (app: App): RowAction[] =>
    can.hidden
      ? []
      : [
          { key: 'edit', label: 'Edit', icon: 'edit', testId: 'app-edit', disabledReason: reason, onSelect: () => setEditing(app) },
          { key: 'archive', label: 'Archive', icon: 'archive', testId: 'app-archive', destructive: true, disabledReason: reason, onSelect: () => setArchiving(app) },
        ];

  const overlays = (
    <>
      {project ? <ConnectAppSheet open={connecting} onOpenChange={setConnecting} project={project} onConnected={(app) => onConnected?.(app)} /> : null}
      <EditAppSheet app={editing} onOpenChange={(o) => (o ? undefined : setEditing(null))} />
      <ArchiveAppDialog app={archiving} onOpenChange={(o) => (o ? undefined : setArchiving(null))} onArchived={onArchived} />
    </>
  );

  return { can, connect: () => setConnecting(true), actionsFor, overlays };
}
